import {Transaction} from "../../models/Transaction";
import { ETransactionType } from "../../shared/ITransaction";
import { Cols, ITransaction, IValue } from "../../shared/Consts";
import TableUtils from "../../portlibs/TableUtils";
import PortUtils from "../../portlibs/PortUtils";
import Holding from "../../models/Holding";
import GUI from "../../gui/GUI";
import Table from "../../gui/Table/Table";
import MenuButton from "../../gui/Menu/MenuButton";
import { TMenuProps } from "../../gui/Menu/Menu";
import SettingsIcon from "../../gui/Icons/SettingsIcon";
import { usePath } from "../../libs/path/PathProvider";
import useConfirm from "../../gui/Dialogs/Confirm";
import PortAccount from "../../models/PortAccount";
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider";
import { useCallback, useState } from "react";
import usePortfolio from "../../hooks/PortfolioProvider";

function className(trans:ITransaction&IValue,col:string,isCash:boolean) {
    if (col==Cols.date || col==Cols.maturity) return "nowrap"
    else if (col==Cols.type || col==Cols.total) switch(trans.type) {
        case ETransactionType.buy:       return (trans.quantity>0 && !isCash) ||(trans.quantity<0 && isCash) ? "red bold" : "teal bold"
        case ETransactionType.dividend:
        case ETransactionType.interest:  return "green"
        case ETransactionType.reinvest:
        case ETransactionType.topup:     return "orange"
        case ETransactionType.adjust:    return trans.quantity < 0 ? "orange" : "green"
    }
    else return PortUtils.cellClass(col, (trans as any)[col])
}

export type TTransTableProps = {
    classNames?:Record<string,any>
    tableName:string
    cols:string[]
    rows:Transaction[]
    footer?:any
    infiniteScroll?:boolean
    holding?:Holding
    sortResolver?:string[]
    balance?:Record<string,number>
}

function DateCell(props:{trans:Transaction}) {
    const [busy,setBusy] = useState<boolean>(false)
    const {confirm} = useConfirm()
    const {addSnackbar} = useSnackbar()
    const {portfolio} = usePortfolio()
    const {setHash} = usePath()

    const onDeleteConfirm = async (dismiss:Function) => {
        try {
            await PortAccount.i.deleteTrans(props.trans)
            dismiss()
        }catch(e){
            addSnackbar(e)
        }
    }
    const onDelete = () => confirm({msg:`Are you sure you wish to delete this transaction?`, onYes:onDeleteConfirm})
    const onDupe = useCallback(async () => {
        if (busy) return
        else setBusy(true)
        try {
            const dupe = props.trans.clone()
            dupe._id = ""
            dupe.date = new Date()
            const {transaction} = await PortAccount.i.update(portfolio, dupe, false)
            setHash(PortUtils.createHash(props.trans.holding.id, "update", transaction._id))
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    },[portfolio])
    
    const menuProps:TMenuProps<string> = {
        options:[
            {id:"update",text:"Update",url:{hash:PortUtils.createHash(props.trans.holding.id, "update", props.trans.id)}},
            {id:"delete",text:"Delete",onClick:onDelete},
            {id:"dup",text:"Duplicate",onClick:onDupe},
        ],
    }
    return <div className="flex middle gap-4">
        <MenuButton menuProps={menuProps} className="icon" size="medium" onClick={e => e.stopPropagation()}><SettingsIcon/></MenuButton>
        {props.trans.value("date")}
    </div>
}

export default function TransactionTable(props:TTransTableProps){
    const {tableName,footer,infiniteScroll,...tableProps} = props
    const {setHash} = usePath()
    const isCash = props.holding?.isCash || false
    const updateTransaction = (row:Transaction) => setHash(PortUtils.createHash(row.holding.id, "update", row.id))
    const cell = (row:Transaction, col:string) => {
        if (col=="date") return <DateCell trans={row}/>
        else if (col=="balance" && props.balance) return PortUtils.formatCurrency(row.holding.currency, props.balance[row._id])
        return row.value(col)
    }
    const cellProp = (row:Transaction, col:string) => ( { className: GUI.joinClass(className(row,col,isCash),TableUtils.align(col)) } )
    const headerProps = (col:string) => ({className:TableUtils.align(col)})
    const row = (row:Transaction) => ({onClick:() => updateTransaction(row)})
    const footers : any[] = []
    if (footer) footers.push(footer)
    return <Table tableName={tableName} {...tableProps} footer={footer ? [footer] : undefined} 
        rowKey={(row:Transaction) => row.id} 
        cellText={cell} td={cellProp} tr={row}
        label={TableUtils.header} th={headerProps} rowsPerPage={20}/>
}
